import { jsx, jsxs } from "react/jsx-runtime";
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  __css,
  OverlayDrawer,
  tokens,
  Tooltip,
  useFocusFinders
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { drawerSize } from "../theme/tokens.js";
const useStyles = __css({"drawer":{"Bjr0ffy":"f2qykdt","D305fb":"f921z2"},"header":{"Dbcxam":"f1fcg080","Behc1zw":"f10zy6vx","uk9mye":"f1p2dij1","mbw9mh":"f1a566p"},"title":{"Btd35i7":"fjksvth"},"actions":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg"},"body":{"rjzwhg":"fqu8h5b"},"content":{"mc9l5x":"f1bxpd7w"}});
const useDrawerStyles = __css({"root":{"Bjr0ffy":"f2qykdt","D305fb":"f921z2"}});
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function useFocusKeeper(open) {
  const bodyRef = useRef(null);
  const { findFirstFocusable } = useFocusFinders();
  useEffect(() => {
    if (!open) return;
    const inDialog = (element) => element !== null && element.closest('[role="dialog"], [role="alertdialog"]') !== null;
    let lastInside = inDialog(document.activeElement) ? document.activeElement : null;
    let timer;
    const strayed = () => lastInside !== null && !lastInside.isConnected && !inDialog(document.activeElement);
    const restore = () => {
      timer = void 0;
      const body = bodyRef.current;
      if (!body || !strayed()) return;
      const drawer = body.closest('[role="dialog"]') ?? body;
      const target = findFirstFocusable(body) ?? findFirstFocusable(drawer) ?? drawer.querySelector(FOCUSABLE);
      target?.focus();
    };
    const check = () => {
      if (timer === void 0 && strayed()) timer = window.setTimeout(restore, 50);
    };
    const onFocusIn = (event) => {
      if (event.target instanceof Element && inDialog(event.target)) lastInside = event.target;
      else check();
    };
    const observer = new MutationObserver(check);
    document.addEventListener("focusin", onFocusIn);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      observer.disconnect();
      if (timer !== void 0) window.clearTimeout(timer);
    };
  }, [open, findFirstFocusable]);
  return bodyRef;
}
function RightPanel({ open, title, onClose, headerActions, children }) {
  const styles = useStyles();
  const { t } = useTranslation("ui");
  const bodyRef = useFocusKeeper(open);
  return /* @__PURE__ */ jsxs(
    OverlayDrawer,
    {
      position: "end",
      open,
      className: styles.drawer,
      "aria-label": title,
      onOpenChange: (_e, data) => {
        if (!data.open) onClose();
      },
      children: [
        /* @__PURE__ */ jsx(DrawerHeader, { className: styles.header, children: /* @__PURE__ */ jsx(
          DrawerHeaderTitle,
          {
            heading: { as: "h2", className: styles.title },
            action: /* @__PURE__ */ jsxs("div", { className: styles.actions, children: [
              headerActions,
              /* @__PURE__ */ jsx(Tooltip, { content: t("shell.rightPanel.close"), relationship: "inaccessible", children: /* @__PURE__ */ jsx(
                Button,
                {
                  appearance: "subtle",
                  icon: /* @__PURE__ */ jsx(Dismiss20Regular, {}),
                  "aria-label": t("shell.rightPanel.close"),
                  onClick: onClose
                }
              ) })
            ] }),
            children: title
          }
        ) }),
        /* @__PURE__ */ jsx(DrawerBody, { className: styles.body, children: /* @__PURE__ */ jsx("div", { ref: bodyRef, className: styles.content, children }) })
      ]
    }
  );
}
export {
  RightPanel,
  useDrawerStyles
};
//# sourceMappingURL=right-panel.js.map

import "/opt/batiflow/app/packages/ui/dist/shell/right-panel.griffel.css";